import { ENV } from '../config/env';
import { RiskProfile } from '../types/risk';
import { supabase } from '../lib/supabase';

/**
 * Service for interacting with the risk manager API endpoints
 */
class RiskManagerService {
  // Get the user ID from Supabase
  private async getUserId(): Promise<string> {
    try {
      const { data } = await supabase.auth.getSession();
      return data.session?.user?.id || '';
    } catch (e) {
      console.error('Error getting Supabase user ID:', e);
      return '';
    }
  }

  /**
   * Get all risk profiles for the current user
   * @returns Promise with an array of risk profiles
   */
  async getAll(): Promise<RiskProfile[]> {
    try {
      const userId = await this.getUserId();
      if (!userId) {
        throw new Error('User ID not available. Please log in again.');
      }
      
      // Use query parameters for GET request instead of body
      const response = await fetch(`${ENV.API_URL}/api/risk-manager/profiles?userId=${encodeURIComponent(userId)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
        // No body for GET request
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error ${response.status}: ${response.statusText}. ${errorText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching risk profiles:', error);
      throw error;
    }
  }

  /**
   * Get risk profiles filtered by criteria
   * @param filters - Filter criteria
   * @returns Promise with filtered risk profiles
   */
  async getFiltered(filters: any): Promise<RiskProfile[]> {
    try {
      const userId = await this.getUserId();
      const queryParams = new URLSearchParams();
      
      // Add all filter parameters to the query
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, String(value));
        }
      });
      
      const response = await fetch(`${ENV.API_URL}/api/risk-manager/profiles/filter?${queryParams}`, {
        method: 'POST', // Changed to POST to include request body
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId, ...filters })
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error fetching filtered risk profiles:', error);
      throw error;
    }
  }

  /**
   * Get a specific risk profile by ID
   * @param id - Risk profile ID
   * @returns Promise with risk profile data
   */
  async getById(id: string): Promise<RiskProfile> {
    try {
      const userId = await this.getUserId();
      const response = await fetch(`${ENV.API_URL}/api/risk-manager/profiles/${id}`, {
        method: 'POST', // Changed to POST to include request body
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId })
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error fetching risk profile ${id}:`, error);
      throw error;
    }
  }

  /**
   * Create a new risk profile
   * @param riskProfile - Risk profile data
   * @returns Promise with created risk profile
   */
  async create(riskProfile: RiskProfile): Promise<RiskProfile> {
    try {
      const userId = await this.getUserId();
      const response = await fetch(`${ENV.API_URL}/api/risk-manager/profiles`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...riskProfile, userId })
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating risk profile:', error);
      throw error;
    }
  }

  /**
   * Update an existing risk profile
   * @param id - Risk profile ID
   * @param riskProfile - Updated risk profile data
   * @returns Promise with updated risk profile
   */
  async update(id: string, riskProfile: RiskProfile): Promise<RiskProfile> {
    try {
      const userId = await this.getUserId();
      const response = await fetch(`${ENV.API_URL}/api/risk-manager/profiles/${id}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ ...riskProfile, userId })
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error(`Error updating risk profile ${id}:`, error);
      throw error;
    }
  }

  /**
   * Delete a risk profile
   * @param id - Risk profile ID
   * @returns Promise with success status
   */
  async delete(id: string): Promise<{ success: boolean }> {
    try {
      const userId = await this.getUserId();
      
      if (!userId) {
        throw new Error('User ID not available. Please log in again.');
      }
      
      console.log(`Deleting risk profile ${id} for user ${userId}`);
      const response = await fetch(`${ENV.API_URL}/api/risk-manager/profiles/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error ${response.status}: ${response.statusText}. ${errorText}`);
      }

      const result = await response.json();
      console.log('Delete response:', result);
      return result;
    } catch (error) {
      console.error(`Error deleting risk profile ${id}:`, error);
      throw error;
    }
  }

  /**
   * Get risk summary statistics
   * @returns Promise with risk summary data
   */
  async getSummary(): Promise<any> {
    try {
      const userId = await this.getUserId();
      
      if (!userId) {
        throw new Error('User ID not available. Please log in again.');
      }
      
      console.log(`Fetching risk summary for userId: ${userId}`);
      const response = await fetch(`${ENV.API_URL}/api/risk-manager/summary?userId=${encodeURIComponent(userId)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error ${response.status}: ${response.statusText}. ${errorText}`);
      }

      const data = await response.json();
      console.log('Risk summary data received:', data);
      return data;
    } catch (error) {
      console.error('Error fetching risk summary:', error);
      throw error;
    }
  }

  /**
   * Delete push notification
   * @param id - Risk profile ID
   * @returns Promise with success status
   */
  async deletePushNotification(id: string): Promise<{success: boolean}> {
    try {
      const userId = await this.getUserId();
      
      if (!userId) {
        throw new Error('User ID not available. Please log in again.');
      }
      
      console.log(`Deleting push notification ${id} for user ${userId}`);
      const response = await fetch(`${ENV.API_URL}/api/risk-manager/profiles/${id}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ userId })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Error ${response.status}: ${response.statusText}. ${errorText}`);
      }

      console.log('Push notification deleted successfully');
      return {success: true};
    } catch (error) {
      console.error('Error deleting push notification:', error);
      throw error;
    }
  }

  /**
   * Generate AI-powered risk mitigation strategies
   * @param riskDetails - Details of the risk to get mitigation strategies for
   * @returns Promise with an array of mitigation strategies
   */
  async generateMitigationStrategies(riskDetails: {
    title: string;
    description: string;
    source: string;
    level: string;
    impactDescription?: string;
    probabilityScore?: number;
    impactScore?: number;
    riskScore?: number;
  }): Promise<{strategies: string[]}> {
    try {
      const response = await fetch(`${ENV.API_URL}/api/risk-manager/mitigation-strategies`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(riskDetails)
      });

      if (!response.ok) {
        throw new Error(`Error ${response.status}: ${response.statusText}`);
      }

      return await response.json();
    } catch (error) {
      console.error('Error generating risk mitigation strategies:', error);
      throw error;
    }
  }
}

// Export a singleton instance
export const riskManagerService = new RiskManagerService();
